import React, {createElement, useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {usePermissions, useTranslate, useGetIdentity} from 'react-admin';
import {ChildIcon, GuardianIcon, SponsoredChildIcon, CurrencyIcon} from '../icons/icons';
import {getCharityDashboard, getDashboard} from './DashboardAPI';
import NewChildren from './NewChildren';
import NewGuardians from './NewGuardians';
import NewPosts from './NewPosts';
import PostChart from './PostChart';
import hasPermission from '../auth/hasPermission';
import {useScope} from '../scope/ScopeContext';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import SectionCard from '../components/statamic/SectionCard';
import EmptyState from '../components/statamic/EmptyState';
import {SkeletonLine} from '../components/statamic/Skeleton';

const StatTile = ({icon, title, value, to, loading}) => (
    <Box
        component={to ? 'a' : 'div'}
        href={to ? `#${to}` : undefined}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            backgroundColor: t.color.surface,
            border: `1px solid ${t.color.border.default}`,
            borderRadius: `${t.radius.md}px`,
            textDecoration: 'none',
            color: 'inherit',
            transition: 'border-color 120ms ease, transform 120ms ease',
            ...(to ? {
                '&:hover': {
                    borderColor: t.color.border.strong,
                    transform: 'translateY(-1px)',
                },
            } : {}),
        }}
    >
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: `${t.radius.md}px`,
                backgroundColor: t.color.accent.primaryTint,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: t.color.accent.primary,
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500}}>
                {title}
            </Typography>
            {loading ? (
                <SkeletonLine width={80} height={20} sx={{mt: 0.5}}/>
            ) : (
                <Typography sx={{fontSize: 22, fontWeight: 700, color: t.color.text.primary, lineHeight: 1.2}}>
                    {value ?? '—'}
                </Typography>
            )}
        </Box>
    </Box>
);

const Dashboard = () => {
    const {permissions} = usePermissions();
    const translate = useTranslate();
    const {identity} = useGetIdentity();
    const {umbrellaId, charityId, umbrellaExternalId, charityExternalId} = useScope();

    const [loaded, setLoaded] = useState(false);
    const [stats, setStats] = useState({
        childrenCount: null,
        childrenSponsoredCount: null,
        guardianCount: null,
        monthlyDonationAmount: null,
        currency: 'EUR',
        postChart: null,
        currentUser: null,
    });

    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        const apply = (data) => {
            if (cancelled) return;
            const currency = data.default_currency || 'EUR';
            let monthlyDonationAmount = null;
            try {
                monthlyDonationAmount = new Intl.NumberFormat(navigator.language, {
                    style: 'currency',
                    currency,
                }).format(data.monthly_donation ?? 0);
            } catch {
                monthlyDonationAmount = String(data.monthly_donation ?? '');
            }
            setStats({
                childrenCount: data.nr_of_children,
                childrenSponsoredCount: data.nr_of_children_sponsored,
                guardianCount: data.nr_of_guardians,
                monthlyDonationAmount,
                currency,
                postChart: data.post_chart,
                currentUser: data.current_user,
            });
            setLoaded(true);
        };

        if (hasPermission(permissions, ['admin'], 'dashboard')) {
            getDashboard(permissions, permissions === 'admin' ? (umbrellaExternalId ?? null) : null, charityExternalId ?? null)
                .then((r) => apply(r.data))
                .catch(() => setLoaded(true));
        } else if (hasPermission(permissions, ['charity'], 'dashboard')) {
            getCharityDashboard()
                .then((r) => apply(r.data))
                .catch(() => setLoaded(true));
        } else {
            setLoaded(true);
        }
        return () => { cancelled = true; };
    }, [permissions, umbrellaExternalId, charityExternalId]);

    const welcomeName = stats.currentUser?.given_name
        ?? identity?.fullName
        ?? localStorage.getItem('username')
        ?? '';

    const canSeeDonations = hasPermission(permissions, ['view'], 'charity.donations');

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                title={welcomeName ? `${translate('Welcome')}, ${welcomeName}` : translate('Dashboard')}
                subtitle={
                    umbrellaId || charityId
                        ? translate('Dashboard is filtered by the scope set in the sidebar.')
                        : translate('A glance at what\'s happening across the platform.')
                }
            />

            <Box sx={{px: 3, pb: 3}}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <StatTile
                        icon={createElement(ChildIcon, {fontSize: 'small'})}
                        title={translate('# of children')}
                        value={stats.childrenCount}
                        to="/children"
                        loading={!loaded}
                    />
                    <StatTile
                        icon={createElement(SponsoredChildIcon, {fontSize: 'small'})}
                        title={translate('# sponsored')}
                        value={stats.childrenSponsoredCount}
                        to="/children"
                        loading={!loaded}
                    />
                    <StatTile
                        icon={createElement(GuardianIcon, {fontSize: 'small'})}
                        title={translate('# of guardians')}
                        value={stats.guardianCount}
                        to="/guardians"
                        loading={!loaded}
                    />
                    {canSeeDonations && (
                        <StatTile
                            icon={<CurrencyIcon currency={stats.currency}/>}
                            title={translate('Monthly donation amount')}
                            value={stats.monthlyDonationAmount}
                            loading={!loaded}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)'},
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <SectionCard title={translate('Newest child profiles')}>
                        <NewChildren umbrellaId={umbrellaId ?? 0} charityId={charityId ?? 0}/>
                    </SectionCard>
                    <SectionCard title={translate('Newest sponsors')}>
                        <NewGuardians
                            umbrellaExternalId={permissions === 'admin' ? umbrellaExternalId : null}
                            charityExternalId={charityExternalId}
                        />
                    </SectionCard>
                </Box>

                <SectionCard title={translate('Latest posts')}>
                    <NewPosts umbrellaId={umbrellaId ?? 0} charityId={charityId ?? 0}/>
                </SectionCard>

                {loaded && (
                    <Box sx={{mt: 2}}>
                        <SectionCard title={translate('Posts from the last 30 days')}>
                            {stats.postChart && Object.keys(stats.postChart).length > 0 ? (
                                <PostChart posts={stats.postChart}/>
                            ) : (
                                <EmptyState
                                    title={translate('No posts in the last 30 days')}
                                    description={translate('Once volunteers start sharing updates, activity will appear here.')}
                                    dense
                                />
                            )}
                        </SectionCard>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Dashboard;
